import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { X, Check, ZoomIn } from "lucide-react";

async function getCroppedBlob(imageSrc, crop) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });
  const size = 800;
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, size, size);
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.9));
}

export default function ImageCropModal({ imageSrc, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_, px) => setPixels(px), []);

  const save = async () => {
    if (!pixels) return;
    setBusy(true);
    const blob = await getCroppedBlob(imageSrc, pixels);
    onCropped(blob);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-foreground/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="crop-modal">
      <div className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head font-extrabold text-xl">Position &amp; crop</h3>
          <button onClick={onCancel} className="p-1.5 border-2 border-foreground rounded-md" data-testid="crop-cancel"><X size={16} /></button>
        </div>
        <div className="relative w-full h-72 bg-muted rounded-lg overflow-hidden border-2 border-foreground">
          <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={1}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
        </div>
        <div className="flex items-center gap-3 mt-4">
          <ZoomIn size={16} />
          <input type="range" min={1} max={3} step={0.05} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))} className="flex-1" data-testid="crop-zoom" />
        </div>
        <div className="flex gap-3 mt-4">
          <button onClick={onCancel} className="flex-1 font-head font-bold border-2 border-foreground rounded-full py-2.5 hover:bg-muted">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="crop-save"
            className="flex-1 font-head font-bold bg-secondary text-secondary-foreground border-2 border-foreground rounded-full py-2.5 flex items-center justify-center gap-2 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60">
            <Check size={15} /> {busy ? "Saving…" : "Use photo"}
          </button>
        </div>
      </div>
    </div>
  );
}
